import React, { useState } from 'react';
import { CreditApplicationStepField, CreditApplicationTemplate } from '@dill/dill-shared';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { CaretDown, CaretUp } from 'phosphor-react';

export type SpecificRule = {
  fieldId: string;
  isRequired?: boolean;
  minimumNumberOfGroups?: number;
  minimumNumberOfSignatures?: number;
  minimumCreditAmount?: number;
  hidden?: boolean;
};

interface SpecificRulesSelectorProps {
  templates: CreditApplicationTemplate[];
  rules: SpecificRule[];
  onChange: (rules: SpecificRule[]) => void;
  alwaysOpen?: boolean;
}

type StepEntry = {
  stepName: string;
  fields: CreditApplicationStepField[];
};

const SKIP_FROM_FIELDS = new Set(['AUTOFILL_CHECKBOX', 'CONDITIONAL_FORM']);

const buildCombinedSteps = (templates: CreditApplicationTemplate[]): StepEntry[] => {
  const stepMap = new Map<string, Map<string, CreditApplicationStepField>>();

  templates.forEach((template) => {
    template.steps.slice(1).forEach((step) => {
      if (!stepMap.has(step.name)) {
        stepMap.set(step.name, new Map());
      }
      const fieldMap = stepMap.get(step.name);
      if (!fieldMap) return;

      step.fields.forEach((field) => {
        if (field.stepType === 'NOT_GROUPED') {
          const visibleFields = (field.unGroupedFields ?? []).filter(
            (f) => !SKIP_FROM_FIELDS.has(f.fromField)
          );
          if (visibleFields.length === 0) return;
        }
        const key =
          field.stepType === 'GROUPED'
            ? `group:${field.groupName}`
            : field.unGroupedFields?.map((f) => f.id).join('|') ?? field.groupName ?? '';
        if (!fieldMap.has(key)) {
          fieldMap.set(key, field);
        }
      });
    });
  });

  return Array.from(stepMap.entries()).map(([stepName, fieldMap]) => ({
    stepName,
    fields: Array.from(fieldMap.values()),
  }));
};

export const buildFieldNameMap = (templates: CreditApplicationTemplate[]): Map<string, string> => {
  const names = new Map<string, string>();
  buildCombinedSteps(templates).forEach(({ fields }) => {
    fields.forEach((field) => {
      if (field.stepType === 'GROUPED') {
        names.set(`group:${field.groupName}`, field.groupName ?? '');
        return;
      }
      (field.unGroupedFields ?? [])
        .filter((f) => !SKIP_FROM_FIELDS.has(f.fromField))
        .forEach((f) => names.set(f.id, f.name));
    });
  });
  return names;
};

const RequiredToggle = ({
  value,
  defaultValue,
  onChange,
}: {
  value: boolean | undefined;
  defaultValue?: boolean;
  onChange: (next: boolean | undefined) => void;
}) => {
  const displayValue = value !== undefined ? value : defaultValue;
  const isOverride = value !== undefined;

  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      <button
        type="button"
        onClick={() => {
          if (displayValue === true) {
            if (isOverride) onChange(undefined);
            // clicking the active default — no-op
          } else {
            onChange(true);
          }
        }}
        className={`text-xs px-2 py-1 rounded-md font-medium transition-colors ${
          displayValue === true
            ? 'bg-blue-400 text-white'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}>
        Required
      </button>
      <button
        type="button"
        onClick={() => {
          if (displayValue === false) {
            if (isOverride) onChange(undefined);
          } else {
            onChange(false);
          }
        }}
        className={`text-xs px-2 py-1 rounded-md font-medium transition-colors ${
          displayValue === false
            ? 'bg-blue-400 text-white'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}>
        Optional
      </button>
    </div>
  );
};

const VisibilityToggle = ({
  value,
  onChange,
  hideLabel = 'Hide',
}: {
  value: boolean | undefined;
  onChange: (next: boolean | undefined) => void;
  hideLabel?: string;
}) => {
  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      <button
        type="button"
        onClick={() => onChange(value === false ? undefined : false)}
        title="Force show this field regardless of the template's default visibility rules"
        className={`text-xs px-2 py-1 rounded-md font-medium transition-colors ${
          value === false ? 'bg-blue-400 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}>
        Show
      </button>
      <button
        type="button"
        onClick={() => onChange(value === true ? undefined : true)}
        title="Force hide this field regardless of the template's default visibility rules"
        className={`text-xs px-2 py-1 rounded-md font-medium transition-colors ${
          value === true ? 'bg-blue-400 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}>
        {hideLabel}
      </button>
    </div>
  );
};

const SpecificRulesSelector: React.FC<SpecificRulesSelectorProps> = ({
  templates,
  rules,
  onChange,
  alwaysOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = alwaysOpen || isOpen;
  const steps = buildCombinedSteps(templates);

  if (steps.length === 0) return null;

  const getRule = (fieldId: string): SpecificRule | undefined =>
    rules.find((r) => r.fieldId === fieldId);

  const upsertRule = (fieldId: string, patch: Partial<Omit<SpecificRule, 'fieldId'>>) => {
    const existing = rules.find((r) => r.fieldId === fieldId);
    if (existing) {
      const updated = { ...existing, ...patch };
      if (
        updated.isRequired === undefined &&
        updated.minimumNumberOfGroups === undefined &&
        updated.minimumNumberOfSignatures === undefined &&
        updated.minimumCreditAmount === undefined &&
        updated.hidden === undefined
      ) {
        onChange(rules.filter((r) => r.fieldId !== fieldId));
      } else {
        onChange(rules.map((r) => (r.fieldId === fieldId ? updated : r)));
      }
    } else {
      onChange([...rules, { fieldId, ...patch }]);
    }
  };

  return (
    <div className="flex flex-col rounded-lg border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => !alwaysOpen && setIsOpen((prev) => !prev)}
        className={`flex items-center justify-between px-3 py-2.5 bg-gray-50 transition-colors w-full text-left ${
          !alwaysOpen ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default'
        }`}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Specific Rules</span>
          <span className="text-xs text-gray-400 font-normal">— override template defaults</span>
          {rules.length > 0 && (
            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
              {rules.length} rule{rules.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {!alwaysOpen &&
          (open ? (
            <CaretUp size={14} className="text-gray-400 flex-shrink-0" />
          ) : (
            <CaretDown size={14} className="text-gray-400 flex-shrink-0" />
          ))}
      </button>

      {open && (
        <div className="flex flex-col gap-3 bg-gray-50 border-t border-gray-200 p-3">
          {steps.map(({ stepName, fields }) => (
            <div key={stepName} className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {stepName}
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <div className="flex flex-col gap-1.5">
                {fields.map((field, i) => {
                  if (field.stepType === 'GROUPED') {
                    const fieldId = `group:${field.groupName}`;
                    const rule = getRule(fieldId);
                    return (
                      <div
                        key={`${fieldId}-${i}`}
                        className={`flex items-center gap-3 bg-white border rounded-lg px-3 py-2.5 ${
                          rule !== undefined ? 'border-blue-300' : 'border-gray-100'
                        }`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">
                            {field.groupName}
                          </p>
                          <p className="text-xs text-gray-400">
                            Group · default min {field.minimunNumberOfGroups ?? 1}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="w-28">
                            <AppInputField
                              inputType="number"
                              label=""
                              placeholder={`Min (${field.minimunNumberOfGroups ?? 1})`}
                              value={rule?.minimumNumberOfGroups ?? null}
                              onTextChange={(val) => {
                                const n = val ? Number(val) : undefined;
                                upsertRule(fieldId, { minimumNumberOfGroups: n });
                              }}
                            />
                          </div>
                          <RequiredToggle
                            value={rule?.isRequired}
                            onChange={(next) => upsertRule(fieldId, { isRequired: next })}
                          />
                        </div>
                      </div>
                    );
                  }

                  const visibleFields = (field.unGroupedFields ?? []).filter(
                    (f) => !SKIP_FROM_FIELDS.has(f.fromField)
                  );

                  return visibleFields.map((f) => {
                    const fieldId = f.id;
                    const rule = getRule(fieldId);
                    const isInviteUser = f.fromField === 'INVITE_USER';
                    const isDocumentField =
                      f.fromField === 'DOCUMENT_FIELD' || f.fromField === 'DYNAMIC_FORM_DOCUMENT';
                    const isGuarantor = f.type === 'GUARANTOR' || f.type === 'CORPORATE_GUARANTOR';
                    // Hiding the bank reference removes it from the application entirely, along
                    // with its step if nothing else is on it.
                    const isBankReference = f.fromField === 'BANK_REFERENCE';
                    const isBankReferenceRemoved = isBankReference && rule?.hidden === true;
                    return (
                      <div
                        key={fieldId}
                        className={`flex flex-col gap-2 bg-white border rounded-lg px-3 py-2.5 ${
                          rule !== undefined ? 'border-blue-300' : 'border-gray-100'
                        }`}>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">{f.name}</p>
                            <p className="text-xs text-gray-400">
                              {isInviteUser
                                ? f.type === 'GUARANTOR' || f.type === 'CORPORATE_GUARANTOR'
                                  ? 'Guarantor signature'
                                  : f.type === 'AUTHORIZED_AGENT'
                                  ? 'Authorized agent signature'
                                  : 'Signature'
                                : isDocumentField
                                ? 'Document'
                                : isBankReferenceRemoved
                                ? 'Removed from the application'
                                : f.isRequired !== false
                                ? 'Required by default'
                                : 'Optional by default'}
                            </p>
                          </div>
                          {!isInviteUser && !isDocumentField && !isBankReferenceRemoved && (
                            <RequiredToggle
                              value={rule?.isRequired}
                              defaultValue={f.isRequired !== false}
                              onChange={(next) => upsertRule(fieldId, { isRequired: next })}
                            />
                          )}
                          {(isInviteUser || isDocumentField || isBankReference) && (
                            <VisibilityToggle
                              value={rule?.hidden}
                              onChange={(next) => upsertRule(fieldId, { hidden: next })}
                              hideLabel={isBankReference ? 'Remove' : 'Hide'}
                            />
                          )}
                        </div>
                        {isInviteUser && (
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <AppInputField
                                inputType="number"
                                label=""
                                placeholder={`Min signatures (${f.minimumNumberOfSignatures ?? 1})`}
                                value={rule?.minimumNumberOfSignatures ?? null}
                                onTextChange={(val) =>
                                  upsertRule(fieldId, {
                                    minimumNumberOfSignatures: val ? Number(val) : undefined,
                                  })
                                }
                              />
                            </div>
                            {isGuarantor && (
                              <div className="flex-1">
                                <AppInputField
                                  inputType="number"
                                  label=""
                                  placeholder={`Min amount (${f.minimumCreditAmount ?? 0})`}
                                  value={rule?.minimumCreditAmount ?? null}
                                  onTextChange={(val) =>
                                    upsertRule(fieldId, {
                                      minimumCreditAmount: val ? Number(val) : undefined,
                                    })
                                  }
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpecificRulesSelector;
